import { ChangeEvent, memo, useEffect, useState } from 'react';
import {
  Stack,
  Typography,
  Box,
  CircularProgress,
  Avatar,
  Button,
} from '@mui/material';
import { FileUploadOutlined, CameraAlt } from '@mui/icons-material';
import useToast from '@hooks/useToast';
import ImageViewModal from '../../../../api/modal/image-view.modal';

const VisuallyHiddenInput = ({ ...props }) => (
  <input
    style={{
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: 0,
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0,0,0,0)',
      border: 0,
    }}
    {...props}
  />
);

const FOProfileAvatar = ({
  isLoading,
  fileUrl,
  handleUploadPhoto,
  handleOpenModalTakePhoto,
}: {
  isLoading: boolean;
  fileUrl: string;
  handleUploadPhoto: (
    fileData: File,
    fileUrl: string,
    fileName: string,
  ) => void;
  handleOpenModalTakePhoto: () => void;
}) => {
  const { toast } = useToast();
  const [newFileUrl, setNewFileUrl] = useState<any>('');
  const [fileData, setFileData] = useState<File | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const handleViewPhoto = () => {
    if (!newFileUrl) return;
    setIsViewModalOpen(true);
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;
    const newFileName = `profiles_${Date.now()}.${file.name.split('.').pop()}`;

    try {
      const renamedFile = new File(
        [
          await file
            .arrayBuffer()
            .then((buffer) => new Blob([buffer], { type: file.type })),
        ],
        newFileName,
        { type: file.type, lastModified: file.lastModified },
      );

      // add validation for file size 3.1 MB
      if (renamedFile.size > 3.1 * 1024 * 1024) {
        toast({
          message: `Uploaded file exceeded maximum file size.`,
          severity: 'warning',
        });
        return;
      }

      handleUploadPhoto(
        renamedFile,
        URL.createObjectURL(renamedFile),
        newFileName,
      );
      setFileData(renamedFile);
    } catch (error) {
      console.error('Error renaming file:', error);
    }
  };

  useEffect(() => {
    (async () => {
      if (!fileData) return;

      setNewFileUrl(URL.createObjectURL(fileData));
    })();
  }, [fileData]);

  useEffect(() => {
    setNewFileUrl(fileUrl);
  }, [fileUrl]);

  return (
    <Stack gap='16px'>
      <Stack gap={0} justifyContent='center' alignItems='center'>
        <Stack direction='row' alignItems='center'>
          <Typography
            sx={{
              flexGrow: 1,
              fontSize: '12px',
              color: 'gray',
              textAlign: 'center',
            }}
          >
            <Stack
              direction='column'
              alignItems='center'
              justifyContent='center'
              sx={{
                width: '200px',
                height: '200px',
                borderRadius: '8px',
                border: '1px dashed lightgray',
                padding: '5px',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '190px',
                  height: '190px',
                  '&:hover .hover-buttons, .hover-buttons-no-file': {
                    visibility: 'visible',
                    opacity: 1,
                  },
                  '&:hover #account_avatar, .hover-buttons:hover + #account_avatar':
                    {
                      opacity: 0.5,
                    },
                }}
              >
                <>
                  {isLoading ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        left: 0,
                        zIndex: 2,
                        opacity: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                      }}
                    >
                      <CircularProgress size={20} />
                      <Typography sx={{ ml: 1 }}>Validating file...</Typography>
                    </Box>
                  ) : (
                    <>
                      {newFileUrl ? (
                        <img
                          src={newFileUrl}
                          alt='avatar'
                          id='account_avatar'
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            // transform: 'scaleX(-1)',
                          }}
                        />
                      ) : (
                        <Avatar
                          id='account_avatar'
                          sx={{
                            backgroundColor: '#F5F5F5',
                            width: '100%',
                            height: '100%',
                            borderRadius: '8px',
                            border: '1px dashed lightgray',
                            padding: '5px',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 1,
                          }}
                        />
                      )}
                    </>
                  )}
                </>
                <Stack
                  direction='column'
                  alignItems='center'
                  justifyContent='center'
                  spacing={1}
                  className={
                    fileUrl ? 'hover-buttons' : 'hover-buttons-no-file'
                  }
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    width: '100%',
                    visibility: fileUrl ? 'hidden' : 'visible',
                    opacity: fileUrl ? 0 : 1,
                    transition: 'visibility 0s, opacity 0.5s linear',
                  }}
                >
                  {!isLoading && (
                    <>
                      {newFileUrl && (
                        <Button
                          variant='outlined'
                          sx={{
                            border: '1px solid',
                            backgroundColor: '#ffffff',
                            fontSize: '14px',
                            borderRadius: '10px',
                            width: '160px',
                            pointerEvents: 'auto',
                            zIndex: 3,
                          }}
                          onClick={handleViewPhoto}
                        >
                          View
                        </Button>
                      )}
                      <Button
                        component='label'
                        variant='outlined'
                        sx={{
                          border: '1px solid',
                          backgroundColor: '#ffffff',
                          fontSize: '14px',
                          borderRadius: '10px',
                          width: '160px',
                          pointerEvents: 'auto',
                          zIndex: 3,
                        }}
                        startIcon={<FileUploadOutlined />}
                      >
                        Upload Photo
                        <VisuallyHiddenInput
                          name='image'
                          type='file'
                          accept='image/*'
                          onChange={handleFileChange}
                        />
                      </Button>
                      <Button
                        component='label'
                        variant='outlined'
                        sx={{
                          border: '1px solid',
                          backgroundColor: '#ffffff',
                          fontSize: '14px',
                          borderRadius: '10px',
                          width: '160px',
                          pointerEvents: 'auto',
                          zIndex: 3,
                        }}
                        startIcon={<CameraAlt />}
                        onClick={handleOpenModalTakePhoto}
                      >
                        Take a Photo
                      </Button>
                    </>
                  )}
                </Stack>
              </Box>
            </Stack>
            <Box mt={1} />
            Allowed *.jpeg, *jpg, *.png <br />
            Max size of 3.1 MB
          </Typography>
        </Stack>
      </Stack>
      <ImageViewModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        imageUrl={newFileUrl}
      />
    </Stack>
  );
};

export default memo(FOProfileAvatar);
